import React from "react";
import { Card, CardBody, CardHeader, Table, Button, Row, Col, Dropdown, OverlayTrigger, Tooltip, Alert } from "react-bootstrap";
import moment from "moment";
import {
  getMonthsBetween,
  getWeeksBetween,
  getWeeklyGroups,
} from '../../utils/scheduleGenerators/dateHelpers';

const RotationScheduleTab = ({
  schedules,
  getCurrentRotationSchedules,
  isRotationEditMode,
  isBlockViewExpanded,
  setIsBlockViewExpanded,
  blockPageIndices,
  setBlockPageIndices,
  blockPattern,
  blockDuration,
  weekDuration,
  rotationColors,
  rotationTypes,
  getRotationLabel,
  abbreviateRotation,
  renderResidentNameWithPic,
  getRotationColor,
  movedRotations,
  affectedRotationCells,
  draggedRotation,
  offServiceResidents,
  handleDeleteClick,
  exportToCalendar,
  exportToExcel,
  exportToPrint,
  exportAllToExcel,
  exportAllToPrint,
  viewAcademicYear,
  handleBlockRotationDrop,
  handleBlockRotationChange,
  handleBlockRotationSwap,
  handleRotationChange,
  handleRotationDragStart,
  handleRotationDragOver,
  handleRotationDrop,
  handleRotationTypeDragStart,
  handleOpenOffServiceModal,
  handleEditOffServiceResidents,
  setEditedRotationSchedules,
}) => {
  // Group schedules by type
  const schedulesByType = {
    monthly: [],
    weekly: [],
    blocks: [],
  };

  Object.entries(schedules).forEach(([pgyLevel, scheduleData]) => {
    const scheduleType = getCurrentRotationSchedules()[pgyLevel]?.scheduleType || 'monthly';
    schedulesByType[scheduleType].push({ pgyLevel, scheduleData });
  });

  if (Object.keys(schedules).length === 0) {
    return (
      <Card className="mb-4 shadow-sm border-0 py-4">
        <CardBody className="p-0">
          <div className="text-center py-4">
            <div className="d-flex flex-column align-items-center gap-2">
              <img src="/images/undraw_schedule_6t8k.svg" alt="No rotation schedule" className="img-fluid" style={{height: "200px"}} />
              <h5>No Rotation Schedules</h5>
              <p className="text-muted">
                Create your first rotation schedule to get started
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {/* Monthly Schedules Card */}
      {schedulesByType.monthly.length > 0 && (
        <div className="row">
        <div className="col-12">
            {schedulesByType.monthly.map(({ pgyLevel, scheduleData }) => (
              <MonthlyScheduleCard
                key={pgyLevel}
                pgyLevel={pgyLevel}
                scheduleData={scheduleData}
                getCurrentRotationSchedules={getCurrentRotationSchedules}
                isRotationEditMode={isRotationEditMode}
                rotationColors={rotationColors}
                rotationTypes={rotationTypes}
                getRotationLabel={getRotationLabel}
                abbreviateRotation={abbreviateRotation}
                renderResidentNameWithPic={renderResidentNameWithPic}
                getRotationColor={getRotationColor}
                movedRotations={movedRotations}
                affectedRotationCells={affectedRotationCells}
                draggedRotation={draggedRotation}
                offServiceResidents={offServiceResidents}
                handleDeleteClick={handleDeleteClick}
                exportToCalendar={exportToCalendar}
                exportToExcel={exportToExcel}
                exportToPrint={exportToPrint}
                exportAllToExcel={exportAllToExcel}
                exportAllToPrint={exportAllToPrint}
                viewAcademicYear={viewAcademicYear}
                handleRotationChange={handleRotationChange}
                handleRotationDragStart={handleRotationDragStart}
                handleRotationDragOver={handleRotationDragOver}
                handleRotationDrop={handleRotationDrop}
                handleOpenOffServiceModal={handleOpenOffServiceModal}
                handleEditOffServiceResidents={handleEditOffServiceResidents}
                setEditedRotationSchedules={setEditedRotationSchedules}
              />
            ))}
          </div>
        </div>
      )}

      {/* Weekly Schedules Card */}
      {schedulesByType.weekly.length > 0 && (
        <div className="row">
        <div className="col-12">
            {schedulesByType.weekly.map(({ pgyLevel, scheduleData }) => (
              <WeeklyScheduleCard
                key={pgyLevel}
                pgyLevel={pgyLevel}
                scheduleData={scheduleData}
                getCurrentRotationSchedules={getCurrentRotationSchedules}
                isRotationEditMode={isRotationEditMode}
                weekDuration={weekDuration}
                rotationColors={rotationColors}
                rotationTypes={rotationTypes}
                getRotationLabel={getRotationLabel}
                abbreviateRotation={abbreviateRotation}
                renderResidentNameWithPic={renderResidentNameWithPic}
                offServiceResidents={offServiceResidents}
                handleDeleteClick={handleDeleteClick}
                exportToCalendar={exportToCalendar}
                exportToExcel={exportToExcel}
                exportToPrint={exportToPrint}
                exportAllToExcel={exportAllToExcel}
                exportAllToPrint={exportAllToPrint}
                viewAcademicYear={viewAcademicYear}
                handleRotationChange={handleRotationChange}
                handleRotationDragStart={handleRotationDragStart}
                handleRotationDragOver={handleRotationDragOver}
                handleRotationDrop={handleRotationDrop}
                handleOpenOffServiceModal={handleOpenOffServiceModal}
                handleEditOffServiceResidents={handleEditOffServiceResidents}
                setEditedRotationSchedules={setEditedRotationSchedules}
              />
            ))}
          </div>
        </div>
      )}

      {/* Block Schedules Card */}
      {schedulesByType.blocks.length > 0 && (
       <div className="row">
        <div className="col-12">
            {schedulesByType.blocks.map(({ pgyLevel, scheduleData }) => (
              <BlockScheduleCard
                key={pgyLevel}
                pgyLevel={pgyLevel}
                scheduleData={scheduleData}
                getCurrentRotationSchedules={getCurrentRotationSchedules}
                isRotationEditMode={isRotationEditMode}
                isBlockViewExpanded={isBlockViewExpanded}
                setIsBlockViewExpanded={setIsBlockViewExpanded}
                blockPageIndices={blockPageIndices}
                setBlockPageIndices={setBlockPageIndices}
                blockPattern={blockPattern}
                blockDuration={blockDuration}
                rotationColors={rotationColors}
                rotationTypes={rotationTypes}
                getRotationLabel={getRotationLabel}
                abbreviateRotation={abbreviateRotation}
                renderResidentNameWithPic={renderResidentNameWithPic}
                draggedRotation={draggedRotation}
                offServiceResidents={offServiceResidents}
                handleDeleteClick={handleDeleteClick}
                exportToCalendar={exportToCalendar}
                exportToExcel={exportToExcel}
                exportToPrint={exportToPrint}
                exportAllToExcel={exportAllToExcel}
                exportAllToPrint={exportAllToPrint}
                viewAcademicYear={viewAcademicYear}
                handleBlockRotationDrop={handleBlockRotationDrop}
                handleBlockRotationChange={handleBlockRotationChange}
                handleBlockRotationSwap={handleBlockRotationSwap}
                handleRotationTypeDragStart={handleRotationTypeDragStart}
                handleOpenOffServiceModal={handleOpenOffServiceModal}
                handleEditOffServiceResidents={handleEditOffServiceResidents}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// Monthly Schedule Card Component
const MonthlyScheduleCard = ({
  pgyLevel,
  scheduleData,
  getCurrentRotationSchedules,
  isRotationEditMode,
  rotationColors,
  rotationTypes,
  getRotationLabel,
  abbreviateRotation,
  renderResidentNameWithPic,
  getRotationColor,
  movedRotations,
  affectedRotationCells,
  draggedRotation,
  offServiceResidents,
  handleDeleteClick,
  exportToCalendar,
  exportToExcel,
  exportToPrint,
  exportAllToExcel,
  exportAllToPrint,
  viewAcademicYear,
  handleRotationChange,
  handleRotationDragStart,
  handleRotationDragOver,
  handleRotationDrop,
  handleOpenOffServiceModal,
  handleEditOffServiceResidents,
}) => {
  return (
    <Card className="mb-3 shadow-sm border-0">
      <CardHeader className="px-4 py-3 d-flex border-0 lex-row justify-content-between align-items-start">
        <div className="d-flex flex-column">
          <h6 className="fw-semibold mb-0">PGY-{pgyLevel} Monthly Schedule</h6>
          <p className="text-muted small mb-0">{moment(scheduleData.startDate).format('MMM D, YYYY')} - {moment(scheduleData.endDate).format('MMM D, YYYY')}</p>
        </div>
        <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content' }}>
          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-${pgyLevel}`}
              onChange={() => handleOpenOffServiceModal(pgyLevel)}
              checked={false}
            />
            <span className="name bg-white-hover px-2">
              <i className="bi bi-person-plus-fill me-1"></i>
              <span className="d-none d-md-inline"></span>
            </span>
          </label>

          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle variant="light-solid" size="sm">
                <i className="bi bi-download"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => exportToCalendar(pgyLevel, scheduleData)}>
                  <i className="bi bi-calendar-plus"></i> Export to Calendar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToExcel(pgyLevel, scheduleData)}>
                  <i className="bi bi-file-earmark-excel"></i> Current level Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToPrint(pgyLevel, scheduleData)}>
                  <i className="bi bi-printer"></i> Print current level
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={exportAllToExcel}>
                  <i className="bi bi-file-earmark-excel"></i> All Levels to Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={exportAllToPrint}>
                  <i className="bi bi-printer"></i> Print All Levels
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item disabled>
                  Viewing: {viewAcademicYear}-{viewAcademicYear + 1}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-${pgyLevel}`}
              onChange={() => handleDeleteClick(pgyLevel)}
            />
            <span className="name bg-white-hover px-3">
              <i className="bi bi-trash"></i>
            </span>
          </label>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <Row>
          <Col md={12} sm={12} style={{ overflowX: 'auto' }}>
            <Table responsive className={`modern-table${isRotationEditMode ? ' editing-mode' : ''}`}>
              <thead className="mb-3">
                <tr className="custom-header-bg">
                  <th className="ps-4 py-3 border-0">Resident</th>
                  {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthStr) => {
                    const [year, monthNum] = monthStr.split("-");
                    const date = new Date(year, parseInt(monthNum) - 1);
                    const monthName = date.toLocaleString("default", { month: "short" });
                    return (
                      <th key={`month-${monthStr}`} className="py-3 border-0 text-center">
                        {`${monthName} ${year}`}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="pgy-header-row">
                  <td colSpan={getMonthsBetween(scheduleData.startDate, scheduleData.endDate).length + 1} className="ps-3 border-0"></td>
                </tr>
                {Object.entries(getCurrentRotationSchedules()[pgyLevel]?.schedule || {})
                  .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                  .map(([residentId, residentData]) => (
                    <tr key={`resident-${residentId}`} className="resident-row">
                      <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                      {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthStr) => (
                        <td
                          key={`${residentId}-${monthStr}`}
                          className="rotation-cell border-0"
                          style={{ padding: '1px 1px' }}
                          onDragOver={isRotationEditMode ? handleRotationDragOver : undefined}
                          onDrop={isRotationEditMode ? () => handleRotationDrop(residentId, monthStr, residentData.rotations[monthStr]) : undefined}
                        >
                          <div
                            className="rotation-content"
                            draggable={isRotationEditMode}
                            onDragStart={isRotationEditMode ? () => handleRotationDragStart(residentId, monthStr, residentData.rotations[monthStr]) : undefined}
                            style={{
                              backgroundColor: (() => {
                                if (!residentData.rotations[monthStr]) return 'transparent';
                                const rotation = residentData.rotations[monthStr];
                                const baseColor = getRotationColor(rotation);
                                if (movedRotations.has(`${residentId}-${monthStr}`)) {
                                  if (!baseColor || baseColor === 'transparent') return baseColor;
                                  const hex = baseColor.replace('#', '');
                                  const r = parseInt(hex.substr(0, 2), 16);
                                  const g = parseInt(hex.substr(2, 2), 16);
                                  const b = parseInt(hex.substr(4, 2), 16);
                                  const darkerR = Math.max(0, r - Math.round(r * 0.3));
                                  const darkerG = Math.max(0, g - Math.round(g * 0.3));
                                  const darkerB = Math.max(0, b - Math.round(b * 0.3));
                                  return `rgb(${darkerR}, ${darkerG}, ${darkerB})`;
                                }
                                return baseColor || 'transparent';
                              })(),
                              color: residentData.rotations[monthStr] ? 'white' : '#6B7280',
                              textAlign: 'center',
                              fontWeight: '600',
                              fontSize: '0.875rem',
                              borderRadius: '6px',
                              minHeight: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: isRotationEditMode ? 'move' : 'pointer',
                              transition: isRotationEditMode ? 'none' : 'all 0.2s ease-in-out',
                              opacity: draggedRotation?.residentId === residentId && draggedRotation?.sourceMonth === monthStr ? 0.5 : 1,
                              ...(movedRotations.has(`${residentId}-${monthStr}`) && {
                                border: '2px solid var(--green)'
                              }),
                              ...(affectedRotationCells.has(`${residentId}-${monthStr}`) && {
                                border: '2px dashed var(--yellow)'
                              })
                            }}
                          >
                            {(() => {
                              const monthsCount = getMonthsBetween(scheduleData.startDate, scheduleData.endDate).length;
                              const rotation = residentData.rotations[monthStr];
                              if (isRotationEditMode) {
                                return (
                                  <Dropdown>
                                    <Dropdown.Toggle
                                      variant="link"
                                      size="sm"
                                      style={{
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        padding: 0,
                                        margin: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <span>
                                        {monthsCount > 6 ? getRotationLabel(rotation) : rotation || '-'}
                                      </span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', width: '180px' }}>
                                      <Dropdown.Item onClick={() => handleRotationChange(residentId, monthStr, null)}>
                                        <i className="bi bi-dash-circle me-1"></i> Clear
                                      </Dropdown.Item>
                                      <Dropdown.Divider />
                                      {rotationTypes.map((rotationType) => (
                                        <Dropdown.Item
                                          key={rotationType}
                                          onClick={() => handleRotationChange(residentId, monthStr, rotationType)}
                                        >
                                          <span>
                                            {monthsCount > 6 ? getRotationLabel(rotationType) : rotationType}
                                          </span>
                                        </Dropdown.Item>
                                      ))}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                );
                              } else {
                                return (
                                  <span>
                                    {monthsCount > 6 ? getRotationLabel(rotation) : rotation || '-'}
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                <tr className="pgy-header-row">
                  <td colSpan={getMonthsBetween(scheduleData.startDate, scheduleData.endDate).length + 1} className="px-3 border-0"></td>
                </tr>
                {/* Off-service residents row */}
                {(() => {
                  const pgyOffServiceResidents = offServiceResidents.filter(osr => osr.pgyLevel.toString() === pgyLevel.toString());
                  if (pgyOffServiceResidents.length === 0) return null;

                  const residentsByMonth = {};
                  pgyOffServiceResidents.forEach(osr => {
                    const month = (osr.month || '').trim();
                    if (!residentsByMonth[month]) {
                      residentsByMonth[month] = [];
                    }
                    residentsByMonth[month].push(osr);
                  });

                  return (
                    <tr key={`off-service-pgy-${pgyLevel}`} className={`off-service-resident-row ${isRotationEditMode ? 'd-none' : ''}`}>
                      <td className="ps-4 border-0">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-bold me-2">Off Service</span>
                          <Button
                            variant="light-lite"
                            size="sm"
                            className="py-0 px-1"
                            onClick={() => handleEditOffServiceResidents(pgyLevel)}
                            title="Edit off-service residents"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        </div>
                      </td>
                      {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthKey) => {
                        const monthResidents = residentsByMonth[monthKey] || [];
                        if (monthResidents.length === 0) {
                          return (
                            <td key={`off-service-${pgyLevel}-${monthKey}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                              <div className="rotation-content off-service-empty" style={{ minHeight: '50px' }}></div>
                            </td>
                          );
                        }

                        const byRotation = {};
                        monthResidents.forEach(osr => {
                          const rot = (osr.rotationName || '').trim();
                          if (!byRotation[rot]) {
                            byRotation[rot] = [];
                          }
                          byRotation[rot].push(osr);
                        });

                        const rotationEntries = Object.entries(byRotation);
                        const baseColor = rotationEntries.length > 0 
                          ? (rotationColors[rotationEntries[0][0]] || '#6B7280')
                          : '#6B7280';

                        return (
                          <td
                            key={`off-service-${pgyLevel}-${monthKey}`}
                            className="rotation-cell border-0"
                            style={{ padding: '1px 1px' }}
                          >
                            <div
                              className="rotation-content"
                              style={{
                                minHeight: '50px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'center',
                                padding: '2px',
                                gap: '2px'
                              }}
                            >
                              {rotationEntries.map(([rotation, residents]) => {
                                const rotationColor = rotationColors[rotation] || baseColor;
                                return (
                                  <div 
                                    key={rotation} 
                                    style={{ 
                                      backgroundColor: rotationColor,
                                      color: 'white',
                                      textAlign: 'center',
                                      fontWeight: '600',
                                      fontSize: '0.875rem',
                                      width: '100%',
                                      boxSizing: 'border-box',
                                      minHeight: '50px',
                                      borderRadius: '6px',
                                      padding: '6px 4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <span>{getRotationLabel(rotation)}</span>
                                    <div className="badge p-1" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                      {residents.map(r => r.residentName).join(', ')}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })()}
              </tbody>
            </Table>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

// Weekly Schedule Card Component
const WeeklyScheduleCard = ({
  pgyLevel,
  scheduleData,
  getCurrentRotationSchedules,
  isRotationEditMode,
  weekDuration,
  rotationColors,
  rotationTypes,
  getRotationLabel,
  abbreviateRotation,
  renderResidentNameWithPic,
  offServiceResidents,
  handleDeleteClick,
  exportToCalendar,
  exportToExcel,
  exportToPrint,
  exportAllToExcel,
  exportAllToPrint,
  viewAcademicYear,
  handleRotationChange,
  handleRotationDragStart,
  handleRotationDragOver,
  handleRotationDrop,
  handleOpenOffServiceModal,
  handleEditOffServiceResidents,
  setEditedRotationSchedules,
}) => {
  const weekDurationValue = getCurrentRotationSchedules()[pgyLevel]?.weekDuration || weekDuration || 1;
  const groups = getWeeklyGroups(scheduleData.startDate, scheduleData.endDate, weekDurationValue);
  const periodsCount = groups.length;

  return (
    <Card className="mb-3 shadow-sm border-0" style={{ borderLeft: '4px solid #198754' }}>
      <CardHeader className="p-4 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: "var(--bs-body-bg)"}}>
        <div>
          <h5>PGY-{pgyLevel} Weekly Schedule</h5>
        </div>
        <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content', minWidth: '230px' }}>
          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-block-${pgyLevel}`}
              onChange={() => handleOpenOffServiceModal(pgyLevel)}
              checked={false}
            />
            <span className="name bg-white-hover px-2">
              <i className="bi bi-person-plus-fill me-1"></i>
              <span className="d-none d-md-inline">Off Service</span>
            </span>
          </label>

          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle variant="light-solid" size="sm">
                <i className="bi bi-download"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => exportToCalendar(pgyLevel, scheduleData)}>
                  <i className="bi bi-calendar-plus"></i> Export to Calendar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToExcel(pgyLevel, scheduleData)}>
                  <i className="bi bi-file-earmark-excel"></i> Current level Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToPrint(pgyLevel, scheduleData)}>
                  <i className="bi bi-printer"></i> Print current level
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={exportAllToExcel}>
                  <i className="bi bi-file-earmark-excel"></i> All Levels to Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={exportAllToPrint}>
                  <i className="bi bi-printer"></i> Print All Levels
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item disabled>
                  Viewing: {viewAcademicYear}-{viewAcademicYear + 1}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-block-${pgyLevel}`}
              onChange={() => handleDeleteClick(pgyLevel)}
              checked={false}
            />
            <span className="name bg-white-hover px-2">
              <i className="bi bi-trash"></i>
            </span>
          </label>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <Row>
          <Col md={12} sm={12} style={{ overflowX: 'auto' }}>
            <Table responsive className={`modern-table${isRotationEditMode ? ' editing-mode' : ''}`}>
              <thead className="mb-3">
                <tr className="custom-header-bg">
                  <th className="ps-4 py-3 border-0">Resident</th>
                  {groups.map((group) => {
                    const startDateObj = new Date(group.start + 'T00:00:00');
                    const endDateObj = new Date(group.end + 'T00:00:00');
                    const label = `${startDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${endDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                    return (
                      <th key={`week-${group.start}`} className="py-3 border-0 text-center">
                        {label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="pgy-header-row">
                  <td colSpan={groups.length + 1} className="ps-3 border-0"></td>
                </tr>
                {Object.entries(getCurrentRotationSchedules()[pgyLevel]?.schedule || {})
                  .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                  .map(([residentId, residentData]) => (
                    <tr key={`resident-${residentId}`} className="resident-row">
                      <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                      {groups.map((group) => {
                        const keyStr = group.start;
                        const weekKeys = group.weeks.map((w) => w.start);
                        const groupRotation = weekKeys.map((wk) => residentData.rotations[wk]).find((r) => r) || null;
                        const baseColor = groupRotation ? rotationColors[groupRotation] : null;
                        return (
                          <td
                            key={`${residentId}-${keyStr}`}
                            className="rotation-cell border-0"
                            style={{ padding: '1px 1px' }}
                            onDragOver={isRotationEditMode ? handleRotationDragOver : undefined}
                            onDrop={isRotationEditMode ? () => handleRotationDrop(residentId, keyStr, groupRotation) : undefined}
                          >
                            <div
                              className="rotation-content"
                              draggable={isRotationEditMode}
                              onDragStart={isRotationEditMode ? () => handleRotationDragStart(residentId, keyStr, groupRotation) : undefined}
                              style={{
                                backgroundColor: baseColor || 'transparent',
                                color: groupRotation ? 'white' : '#6B7280',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                borderRadius: '6px',
                                minHeight: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isRotationEditMode ? 'move' : 'pointer',
                              }}
                            >
                              {(() => {
                                if (isRotationEditMode) {
                                  return (
                                    <Dropdown>
                                      <Dropdown.Toggle
                                        variant="link"
                                        size="sm"
                                        style={{ color: 'inherit', textDecoration: 'none', padding: 0, margin: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        <span>{periodsCount > 8 ? getRotationLabel(groupRotation) : groupRotation || '-'}</span>
                                        <span className={`badge py-1 px-2 ${isRotationEditMode ? 'd-none' : ''}`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks.length}w</span>
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', width: '180px' }}>
                                        <Dropdown.Item onClick={() => {
                                          const weekKeysToClear = group.weeks.map((w) => w.start);
                                          const cloned = JSON.parse(JSON.stringify(getCurrentRotationSchedules()));
                                          let pg = null;
                                          Object.entries(cloned).forEach(([pgy, sd]) => {
                                            if (sd.schedule && sd.schedule[residentId]) pg = pgy;
                                          });
                                          if (!pg) return;
                                          weekKeysToClear.forEach((wk) => {
                                            cloned[pg].schedule[residentId].rotations[wk] = null;
                                          });
                                          setEditedRotationSchedules(cloned);
                                        }}>
                                          <i className="bi bi-dash-circle me-1"></i> Clear
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {rotationTypes.map((rotationType) => (
                                          <Dropdown.Item key={rotationType} onClick={() => {
                                            const weekKeysToSet = group.weeks.map((w) => w.start);
                                            const cloned = JSON.parse(JSON.stringify(getCurrentRotationSchedules()));
                                            let pg = null;
                                            Object.entries(cloned).forEach(([pgy, sd]) => {
                                              if (sd.schedule && sd.schedule[residentId]) pg = pgy;
                                            });
                                            if (!pg) return;
                                            weekKeysToSet.forEach((wk) => {
                                              cloned[pg].schedule[residentId].rotations[wk] = rotationType;
                                            });
                                            setEditedRotationSchedules(cloned);
                                          }}>
                                            <span>{periodsCount > 8 ? getRotationLabel(rotationType) : rotationType}</span>
                                          </Dropdown.Item>
                                        ))}
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  );
                                }
                                return <span>{periodsCount > 8 ? getRotationLabel(groupRotation) : groupRotation || '-'}</span>;
                              })()}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                <tr className="pgy-header-row">
                  <td colSpan={groups.length + 1} className="px-3 border-0"></td>
                </tr>
                {/* Off-service residents row */}
                {(() => {
                  const pgyOffServiceResidents = offServiceResidents.filter(osr => 
                    osr.pgyLevel.toString() === pgyLevel.toString() && 
                    (osr.scheduleType === 'weekly' || !osr.scheduleType)
                  );
                  if (pgyOffServiceResidents.length === 0) return null;

                  const residentsByWeekBlock = {};
                  pgyOffServiceResidents.forEach(osr => {
                    const weekBlock = (osr.weekBlock || '').trim();
                    if (!residentsByWeekBlock[weekBlock]) {
                      residentsByWeekBlock[weekBlock] = [];
                    }
                    residentsByWeekBlock[weekBlock].push(osr);
                  });

                  return (
                    <tr key={`off-service-pgy-${pgyLevel}`} className={`off-service-resident-row ${isRotationEditMode ? 'd-none' : ''}`}>
                      <td className="ps-4 border-0">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-bold me-2">Off Service</span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-0 px-1"
                            onClick={() => handleEditOffServiceResidents(pgyLevel)}
                            title="Edit off-service residents"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        </div>
                      </td>
                      {groups.map((group) => {
                        const weekBlockResidents = residentsByWeekBlock[group.start] || [];
                        if (weekBlockResidents.length === 0) {
                          return (
                            <td key={`off-service-${pgyLevel}-${group.start}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                              <div className="rotation-content off-service-empty" style={{ minHeight: '50px' }}></div>
                            </td>
                          );
                        }

                        const byRotation = {};
                        weekBlockResidents.forEach(osr => {
                          const rot = (osr.rotationName || '').trim();
                          if (!byRotation[rot]) {
                            byRotation[rot] = [];
                          }
                          byRotation[rot].push(osr);
                        });

                        const rotationEntries = Object.entries(byRotation);
                        const baseColor = rotationEntries.length > 0 
                          ? (rotationColors[rotationEntries[0][0]] || '#6B7280')
                          : '#6B7280';

                        return (
                          <td
                            key={`off-service-${pgyLevel}-${group.start}`}
                            className="rotation-cell border-0"
                            style={{ padding: '1px 1px' }}
                          >
                            <div
                              className="rotation-content"
                              style={{
                                minHeight: '50px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'center',
                                padding: '2px',
                                gap: '2px'
                              }}
                            >
                              {rotationEntries.map(([rotation, residents]) => {
                                const rotationColor = rotationColors[rotation] || baseColor;
                                return (
                                  <div 
                                    key={rotation} 
                                    style={{ 
                                      backgroundColor: rotationColor,
                                      color: 'white',
                                      textAlign: 'center',
                                      fontWeight: '600',
                                      fontSize: '0.875rem',
                                      padding: '4px',
                                      borderRadius: '4px',
                                      width: '100%',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    <span>{getRotationLabel(rotation)}</span>
                                    <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                      {residents.map(r => r.residentName).join(', ')}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })()}
              </tbody>
            </Table>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

// Block Schedule Card Component
const BlockScheduleCard = ({
  pgyLevel,
  scheduleData,
  getCurrentRotationSchedules,
  isRotationEditMode,
  isBlockViewExpanded,
  setIsBlockViewExpanded,
  blockPageIndices,
  setBlockPageIndices,
  blockPattern,
  blockDuration,
  rotationColors,
  rotationTypes,
  getRotationLabel,
  abbreviateRotation,
  renderResidentNameWithPic,
  draggedRotation,
  offServiceResidents,
  handleDeleteClick,
  exportToCalendar,
  exportToExcel,
  exportToPrint,
  exportAllToExcel,
  exportAllToPrint,
  viewAcademicYear,
  handleBlockRotationDrop,
  handleBlockRotationChange,
  handleBlockRotationSwap,
  handleRotationTypeDragStart,
  handleOpenOffServiceModal,
  handleEditOffServiceResidents,
}) => {
  const blockPatternValue = getCurrentRotationSchedules()[pgyLevel]?.blockPattern || blockPattern || '4-1';
  const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
  const blockDurationValue = getCurrentRotationSchedules()[pgyLevel]?.blockDuration || blockDuration || 5;
  const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
  const totalBlocks = Math.ceil(weeks.length / blockDurationValue);
  const currentPageIndex = blockPageIndices[pgyLevel] || 0;
  const blocksPerPage = 4;
  const totalPages = Math.ceil(totalBlocks / blocksPerPage);
  const startBlock = currentPageIndex * blocksPerPage + 1;
  const endBlock = Math.min((currentPageIndex + 1) * blocksPerPage, totalBlocks);

  const allBlocks = [];
  for (let i = 0; i < weeks.length; i += blockDurationValue) {
    const blockWeeks = weeks.slice(i, i + blockDurationValue);
    if (blockWeeks.length > 0) {
      const weekStart = Math.floor((i / blockDurationValue) * blockDurationValue) + 1;
      const weekEnd = Math.min(weekStart + blockDurationValue - 1, weeks.length);
      allBlocks.push({
        index: Math.floor(i / blockDurationValue) + 1,
        start: blockWeeks[0].start,
        end: blockWeeks[blockWeeks.length - 1].end,
        weeks: blockWeeks,
        majorWeeks: blockWeeks.slice(0, majorWeeks),
        minorWeeks: blockWeeks.slice(majorWeeks, majorWeeks + minorWeeks),
        allWeeks: blockWeeks,
        label: `Block ${Math.floor(i / blockDurationValue) + 1} (Weeks ${weekStart}-${weekEnd})`
      });
    }
  }

  const visibleBlocks = (isRotationEditMode && isBlockViewExpanded) 
    ? allBlocks 
    : (() => {
        const startIndex = currentPageIndex * blocksPerPage;
        const endIndex = Math.min(startIndex + blocksPerPage, allBlocks.length);
        return allBlocks.slice(startIndex, endIndex);
      })();

  // Get all unique rotations from the schedule to show in legend
  const allRotations = new Set();
  const schedule = getCurrentRotationSchedules()[pgyLevel]?.schedule || {};
  Object.values(schedule).forEach(residentData => {
    if (residentData.rotations) {
      Object.values(residentData.rotations).forEach(rotation => {
        if (rotation) allRotations.add(rotation);
      });
    }
  });

  return (
    <Card className="mb-3 shadow-sm border-0">
      <CardHeader className="px-4 py-3 d-flex border-bottom flex-row justify-content-between align-items-start">
        <div>
          <h6 className="fw-semibold mb-0">PGY-{pgyLevel} Block Schedule</h6>
          {blockPatternValue && (
            <p className="text-muted small mb-0">
              {majorWeeks}-{minorWeeks} Block Schedule Format ({majorWeeks} weeks major rotation + {minorWeeks} week{minorWeeks !== 1 ? 's' : ''} lighter rotation)
            </p>
          )}
        </div>
        <div className="radio-inputs d-flex align-items-center" style={{ width: 'fit-content', minWidth: '230px' }}>
          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-block-${pgyLevel}`}
              onChange={() => handleOpenOffServiceModal(pgyLevel)}
              checked={false}
            />
            <span className="name bg-white-hover px-2">
              <i className="bi bi-person-plus-fill me-1"></i>
              <span className="d-none d-md-inline">Off Service</span>
            </span>
          </label>
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle variant="light-solid" size="sm">
                <i className="bi bi-download"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => exportToCalendar(pgyLevel, scheduleData)}>
                  <i className="bi bi-calendar-plus"></i> Export to Calendar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToExcel(pgyLevel, scheduleData)}>
                  <i className="bi bi-file-earmark-excel"></i> Current level Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => exportToPrint(pgyLevel, scheduleData)}>
                  <i className="bi bi-printer"></i> Print current level
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={exportAllToExcel}>
                  <i className="bi bi-file-earmark-excel"></i> All Levels to Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={exportAllToPrint}>
                  <i className="bi bi-printer"></i> Print All Levels
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item disabled>
                  Viewing: {viewAcademicYear}-{viewAcademicYear + 1}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <label className="radio">
            <input
              type="radio"
              name={`rotation-action-block-${pgyLevel}`}
              onChange={() => handleDeleteClick(pgyLevel)}
              checked={false}
            />
            <span className="name bg-white-hover px-2">
              <i className="bi bi-trash"></i>
            </span>
          </label>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-calendar3"></i>
            <span>
              {isRotationEditMode && isBlockViewExpanded 
                ? `Viewing All ${totalBlocks} Blocks`
                : `Viewing Blocks ${startBlock} - ${endBlock} of ${totalBlocks}`
              }
            </span>
          </div>
          <div className="d-flex gap-2">
            {isRotationEditMode && (
              <Button
                variant={isBlockViewExpanded ? "outline-primary" : "outline-primary"}
                size="sm"
                onClick={() => setIsBlockViewExpanded(!isBlockViewExpanded)}
                title={isBlockViewExpanded ? "Collapse view" : "Expand to show all blocks"}
              >
                <i className={`bi ${isBlockViewExpanded ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'} me-1`}></i>
                {isBlockViewExpanded ? 'Collapse' : 'Expand'}
              </Button>
            )}
            {(!isBlockViewExpanded || !isRotationEditMode) && (
              <div className="radio-inputs d-flex align-items-center" style={{ width: "fit-content" }}>
                <label className="radio">
                  <input
                    type="radio"
                    checked={false}
                    readOnly
                    onClick={() => {
                      setBlockPageIndices(prev => ({
                        ...prev,
                        [pgyLevel]: Math.max(0, (prev[pgyLevel] || 0) - 1)
                      }));
                    }}
                    disabled={currentPageIndex === 0}
                  />
                  <span
                    className="name bg-white-hover px-2"
                    onClick={() => {
                      if (currentPageIndex === 0) return;
                      setBlockPageIndices(prev => ({
                        ...prev,
                        [pgyLevel]: Math.max(0, (prev[pgyLevel] || 0) - 1)
                      }));
                    }}
                  >
                    <i className="bi bi-chevron-left me-1"></i> <span className="d-none d-md-inline">Previous</span>
                  </span>
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    checked={false}
                    readOnly
                    onClick={() => {
                      setBlockPageIndices(prev => ({
                        ...prev,
                        [pgyLevel]: Math.min(totalPages - 1, (prev[pgyLevel] || 0) + 1)
                      }));
                    }}
                    disabled={currentPageIndex >= totalPages - 1}
                  />
                  <span
                    className="name bg-white-hover px-2"
                    onClick={() => {
                      if (currentPageIndex >= totalPages - 1) return;
                      setBlockPageIndices(prev => ({
                        ...prev,
                        [pgyLevel]: Math.min(totalPages - 1, (prev[pgyLevel] || 0) + 1)
                      }));
                    }}
                  >
                    <span className="d-none d-md-inline">Next</span> <i className="bi bi-chevron-right ms-1"></i>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
        <Row>
          <Col md={isBlockViewExpanded ? 11 : 10} sm={12} className="pe-md-0" style={{ overflowX: 'auto' }}>
            <Table responsive className={`modern-table${isRotationEditMode ? ' editing-mode' : ''}`}>
              <thead className="mb-3">
                <tr className="custom-header-bg">
                  <th className="ps-4 py-3 border-0">Resident</th>
                  {visibleBlocks.map((block) => {
                    const startDate = moment(block.start, 'YYYY-MM-DD', true).format('D MMM');
                    const endDate = moment(block.end, 'YYYY-MM-DD', true).format('D MMM');
                    const dateRange = `${startDate} - ${endDate}`;
                    return (
                      <th key={`block-${block.start}`} className="py-3 border-0 text-center">
                        <div>{block.label}</div>
                        <div className="small text-muted mt-1">{dateRange}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="pgy-header-row">
                  <td colSpan={visibleBlocks.length + 1} className="ps-3 border-0"></td>
                </tr>
                {Object.entries(getCurrentRotationSchedules()[pgyLevel]?.schedule || {})
                  .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                  .map(([residentId, residentData]) => (
                    <tr key={`resident-${residentId}`} className="resident-row">
                      <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                      {visibleBlocks.map((block) => {
                        // Group major weeks by rotation
                        const majorRotationGroups = [];
                        let currentRotation = null;
                        let currentGroupStart = 0;
                        
                        block.majorWeeks.forEach((week, idx) => {
                          const rotation = residentData.rotations[week.start];
                          if (rotation !== currentRotation) {
                            if (currentRotation !== null && idx > 0) {
                              majorRotationGroups.push({
                                rotation: currentRotation,
                                startIdx: currentGroupStart,
                                endIdx: idx - 1,
                                weeks: idx - currentGroupStart
                              });
                            }
                            currentRotation = rotation;
                            currentGroupStart = idx;
                          }
                        });
                        if (currentRotation !== null) {
                          majorRotationGroups.push({
                            rotation: currentRotation,
                            startIdx: currentGroupStart,
                            endIdx: block.majorWeeks.length - 1,
                            weeks: block.majorWeeks.length - currentGroupStart
                          });
                        }
                        
                        // Group minor weeks by rotation
                        const minorRotationGroups = [];
                        currentRotation = null;
                        currentGroupStart = 0;
                        
                        block.minorWeeks.forEach((week, idx) => {
                          const rotation = residentData.rotations[week.start];
                          if (rotation !== currentRotation) {
                            if (currentRotation !== null && idx > 0) {
                              minorRotationGroups.push({
                                rotation: currentRotation,
                                startIdx: currentGroupStart,
                                endIdx: idx - 1,
                                weeks: idx - currentGroupStart
                              });
                            }
                            currentRotation = rotation;
                            currentGroupStart = idx;
                          }
                        });
                        if (currentRotation !== null) {
                          minorRotationGroups.push({
                            rotation: currentRotation,
                            startIdx: currentGroupStart,
                            endIdx: block.minorWeeks.length - 1,
                            weeks: block.minorWeeks.length - currentGroupStart
                          });
                        }
                        
                        const totalWeeks = majorWeeks + minorWeeks;
                        
                        return (
                          <td
                            key={`${residentId}-block-${block.start}`}
                            className="rotation-cell border-0"
                            style={{ 
                              padding: '2px ',
                              userSelect: isRotationEditMode ? 'none' : 'auto',
                              WebkitUserSelect: isRotationEditMode ? 'none' : 'auto'
                            }}
                            onDragOver={isRotationEditMode ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            } : undefined}
                            onDrop={isRotationEditMode ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (draggedRotation && draggedRotation.sourceType === 'rotationType') {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const width = rect.width;
                                const isMajor = x < (width * majorWeeks / totalWeeks);
                                const targetWeekStart = isMajor ? (block.majorWeeks[0]?.start || block.minorWeeks[0]?.start) : (block.minorWeeks[0]?.start || block.majorWeeks[0]?.start);
                                handleBlockRotationDrop(residentId, block.start, targetWeekStart, isMajor ? 'major' : 'minor', null);
                              }
                            } : undefined}
                          >
                            <div 
                              className={isRotationEditMode && majorRotationGroups && majorRotationGroups.length === 2 && majorRotationGroups[0]?.weeks === 2 && majorRotationGroups[1]?.weeks === 2 ? 'block-rotation-container' : ''}
                              style={{ display: 'flex', flexDirection: 'row', gap: '2px', minHeight: '50px', width: '100%', position: 'relative' }}
                            >
                              {/* Swap button for two 2-week rotations - show on hover */}
                              {(() => {
                                const showSwapButton = isRotationEditMode && 
                                  majorRotationGroups && 
                                  majorRotationGroups.length === 2 && 
                                  majorRotationGroups[0]?.weeks === 2 && 
                                  majorRotationGroups[1]?.weeks === 2 && 
                                  handleBlockRotationSwap;
                                
                                return showSwapButton ? (
                                  <Button
                                    variant="solid-info"
                                    size="sm"
                                    className=" position-absolute"
                                    style={{ 
                                      top: '50%',
                                      left: '40%',
                                      transform: 'translate(-50%, -50%)',
                                      zIndex: 100,
                                      padding: '0px 4px',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleBlockRotationSwap(residentId, block.start);
                                    }}
                                    title="Swap rotation order"
                                  >
                                    <i className="bi bi-arrow-left-right" style={{ fontSize: '0.65rem' }}></i>
                                  </Button>
                                ) : null;
                              })()}
                              {/* Major rotation boxes */}
                              {majorRotationGroups.length > 0 ? (
                                majorRotationGroups.map((group, groupIdx) => {
                                  const rotationColor = rotationColors[group.rotation] || 'transparent';
                                  const groupWidthPercent = (group.weeks / totalWeeks) * 100;
                                  const firstWeek = block.majorWeeks[group.startIdx];
                                  const lastWeek = block.majorWeeks[group.endIdx];
                                  const startDate = firstWeek ? moment(firstWeek.start, 'YYYY-MM-DD').format('MMM D, YYYY') : '';
                                  const endDate = lastWeek ? moment(lastWeek.end, 'YYYY-MM-DD').format('MMM D, YYYY') : '';
                                  const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : '';
                                  
                                  const rotationDiv = (
                                    <div
                                      key={`major-${groupIdx}`}
                                      className="custom-rotation-link-btn d-flex flex-row align-items-center justify-content-center gap-2 block-rotation-cell"
                                      style={{
                                        backgroundColor: rotationColor,
                                        color: group.rotation ? 'white' : '#6B7280',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.75rem',
                                        borderRadius: '6px',
                                        padding: '6px 4px',
                                        minHeight: '40px',
                                        width: `${groupWidthPercent}%`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        position: 'relative',
                                        userSelect: isRotationEditMode ? 'none' : 'auto',
                                        WebkitUserSelect: isRotationEditMode ? 'none' : 'auto',
                                      }}
                                    >
                                      {isRotationEditMode && group.rotation && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 py-0 position-absolute top-0 end-0 block-rotation-delete-btn"
                                          style={{ opacity: 0, transition: 'opacity 0.2s' }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBlockRotationChange(residentId, block.start, firstWeek.start, 'major', null);
                                          }}
                                        >
                                          <i className="bi bi-x"></i>
                                        </Button>
                                      )}
                                      {isRotationEditMode && group.rotation ? (
                                        <Dropdown>
                                          <Dropdown.Toggle
                                            variant="link"
                                            size="sm"
                                            className="no-arrow"
                                            style={{ 
                                              color: 'inherit', 
                                              textDecoration: 'none', 
                                              padding: 0, 
                                              margin: 0, 
                                              width: '100%', 
                                              height: '100%', 
                                              display: 'flex', 
                                              flexDirection: 'column',
                                              alignItems: 'center', 
                                              justifyContent: 'center',
                                              gap: '2px',
                                              cursor: 'pointer',
                                              border: 'none',
                                              background: 'transparent'
                                            }}
                                          >
                                            <span>{abbreviateRotation(group.rotation)}</span>
                                            <span className={`${isRotationEditMode ? 'd-none' : ''} badge py-1 px-2`} style={{ fontSize: '0.65rem', opacity: 0.3, backgroundColor: '#ffffff10' }}>{group.weeks}w</span>
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', width: '180px' }}>
                                            {rotationTypes.filter(rt => {
                                              const config = getCurrentRotationSchedules()[pgyLevel]?.blockRotations?.[rt];
                                              return !config || config.column === 'major';
                                            }).map((rotationType) => (
                                              <Dropdown.Item 
                                                key={rotationType} 
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleBlockRotationChange(residentId, block.start, firstWeek.start, 'major', rotationType);
                                                }}
                                              >
                                                <span>{abbreviateRotation(rotationType)}</span>
                                              </Dropdown.Item>
                                            ))}
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      ) : (
                                        <>
                                          <span>{group.rotation ? abbreviateRotation(group.rotation) : '-'}</span>
                                          <span className={`${isRotationEditMode ? 'd-none' : ''} badge py-1 px-2`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks}w</span>
                                        </>
                                      )}
                                    </div>
                                  );
                                  
                                  return dateRange ? (
                                    isRotationEditMode ? rotationDiv : (
                                      <OverlayTrigger 
                                        key={`major-tooltip-${groupIdx}`}
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-major-${residentId}-${block.start}-${groupIdx}`}>{dateRange}</Tooltip>}
                                      >
                                        {rotationDiv}
                                      </OverlayTrigger>
                                    )
                                  ) : rotationDiv;
                                })
                              ) : (
                                <div
                                  style={{
                                    border: '2px dashed var(--border-bg)',
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    padding: '6px 4px',
                                    minHeight: '40px',
                                    width: `${(majorWeeks / totalWeeks) * 100}%`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'row',
                                    gap: '2px',
                                  }}
                                >
                                  <span>-</span>
                                  <span className={`${isRotationEditMode ? 'd-none' : ''} badge py-1 px-2`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{majorWeeks}w</span>
                                </div>
                              )}
                              
                              {/* Minor rotation boxes */}
                              {minorRotationGroups.length > 0 ? (
                                minorRotationGroups.map((group, groupIdx) => {
                                  const rotationColor = rotationColors[group.rotation] || 'transparent';
                                  const groupWidthPercent = (group.weeks / totalWeeks) * 100;
                                  const firstWeek = block.minorWeeks[group.startIdx];
                                  const lastWeek = block.minorWeeks[group.endIdx];
                                  const startDate = firstWeek ? moment(firstWeek.start, 'YYYY-MM-DD').format('MMM D, YYYY') : '';
                                  const endDate = lastWeek ? moment(lastWeek.end, 'YYYY-MM-DD').format('MMM D, YYYY') : '';
                                  const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : '';
                                  
                                  const rotationDiv = (
                                    <div
                                      key={`minor-${groupIdx}`}
                                      style={{
                                        backgroundColor: rotationColor,
                                        color: group.rotation ? 'white' : '#6B7280',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.75rem',
                                        borderRadius: '6px',
                                        padding: '6px 4px',
                                        minHeight: '40px',
                                        width: `${groupWidthPercent}%`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: '2px',
                                        position: 'relative',
                                        userSelect: isRotationEditMode ? 'none' : 'auto',
                                        WebkitUserSelect: isRotationEditMode ? 'none' : 'auto',
                                      }}
                                      className="custom-rotation-link-btn block-rotation-cell"
                                    >
                                      {isRotationEditMode && group.rotation && (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 py-0 position-absolute top-0 end-0 block-rotation-delete-btn"
                                          style={{ opacity: 0, transition: 'opacity 0.2s' }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleBlockRotationChange(residentId, block.start, firstWeek.start, 'minor', null);
                                          }}
                                        >
                                          <i className="bi bi-x"></i>
                                        </Button>
                                      )}
                                      {isRotationEditMode && group.rotation ? (
                                        <Dropdown>
                                          <Dropdown.Toggle
                                            variant="link"
                                            size="sm"
                                            className="no-arrow"
                                            style={{ 
                                              color: 'inherit', 
                                              textDecoration: 'none', 
                                              padding: 0, 
                                              margin: 0, 
                                              width: '100%', 
                                              height: '100%', 
                                              display: 'flex', 
                                              flexDirection: 'column',
                                              alignItems: 'center', 
                                              justifyContent: 'center',
                                              gap: '2px',
                                              cursor: 'pointer',
                                              border: 'none',
                                              background: 'transparent'
                                            }}
                                          >
                                            <span>{abbreviateRotation(group.rotation)}</span>
                                            <span className={`badge py-1 px-2 ${isRotationEditMode ? 'd-none' : ''}`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks}w</span>
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto', width: '180px' }}>
                                            {rotationTypes.filter(rt => {
                                              const config = getCurrentRotationSchedules()[pgyLevel]?.blockRotations?.[rt];
                                              return !config || config.column === 'minor';
                                            }).map((rotationType) => (
                                              <Dropdown.Item 
                                                key={rotationType} 
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleBlockRotationChange(residentId, block.start, firstWeek.start, 'minor', rotationType);
                                                }}
                                              >
                                                <span>{abbreviateRotation(rotationType)}</span>
                                              </Dropdown.Item>
                                            ))}
                                          </Dropdown.Menu>
                                        </Dropdown>
                                      ) : (
                                        <>
                                          <span>{group.rotation ? abbreviateRotation(group.rotation) : '-'}</span>
                                          <span className={`${isRotationEditMode ? 'd-none' : ''} badge py-1 px-2`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks}w</span>
                                        </>
                                      )}
                                    </div>
                                  );
                                  
                                  return dateRange ? (
                                    isRotationEditMode ? rotationDiv : (
                                      <OverlayTrigger
                                        key={`minor-tooltip-${groupIdx}`}
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-minor-${residentId}-${block.start}-${groupIdx}`}>{dateRange}</Tooltip>}
                                      >
                                        {rotationDiv}
                                      </OverlayTrigger>
                                    )
                                  ) : rotationDiv;
                                })
                              ) : (
                                <div
                                  style={{
                                    border: '2px dashed var(--border-bg)',
                                    color: '#6B7280',
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    padding: '6px 4px',
                                    minHeight: '40px',
                                    width: `${(minorWeeks / totalWeeks) * 100}%`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'row',
                                    gap: '2px',
                                  }}
                                >
                                  <span>-</span>
                                  <span className={`${isRotationEditMode ? 'd-none' : ''} badge py-1 px-2`} style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{minorWeeks}w</span>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                <tr className="pgy-header-row">
                  <td colSpan={visibleBlocks.length + 1} className="px-3 border-0"></td>
                </tr>
                {/* Off-service residents row */}
                {(() => {
                  const pgyOffServiceResidents = offServiceResidents.filter(osr => 
                    osr.pgyLevel.toString() === pgyLevel.toString() && 
                    (osr.scheduleType === 'blocks' || (!osr.scheduleType && !osr.month && !osr.weekBlock))
                  );
                  if (pgyOffServiceResidents.length === 0) return null;

                  const residentsByBlock = {};
                  pgyOffServiceResidents.forEach(osr => {
                    const blockIndex = (osr.blockIndex || '').toString();
                    if (!residentsByBlock[blockIndex]) {
                      residentsByBlock[blockIndex] = [];
                    }
                    residentsByBlock[blockIndex].push(osr);
                  });

                  return (
                    <tr key={`off-service-pgy-${pgyLevel}`} className={`off-service-resident-row ${isRotationEditMode ? 'd-none' : ''}`}>
                      <td className="ps-4 border-0">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-bold me-2">Off Service</span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-0 px-1"
                            onClick={() => handleEditOffServiceResidents(pgyLevel)}
                            title="Edit off-service residents"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        </div>
                      </td>
                      {visibleBlocks.map((block) => {
                        const blockResidents = residentsByBlock[block.index.toString()] || [];
                        
                        // Group by major and minor rotations
                        const majorResidents = blockResidents.filter(osr => {
                          const hasMajor = !!(osr.majorRotation || (osr.rotations && osr.rotations.major));
                          return hasMajor;
                        });
                        
                        const minorResidents = blockResidents.filter(osr => {
                          const hasMinor = !!(osr.minorRotation || (osr.rotations && osr.rotations.minor));
                          return hasMinor;
                        });

                        const totalWeeks = majorWeeks + minorWeeks;

                        return (
                          <td
                            key={`off-service-${pgyLevel}-${block.start}`}
                            className="rotation-cell border-0"
                            style={{ 
                              padding: '2px',
                              userSelect: 'auto'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '2px', minHeight: '50px', width: '100%' }}>
                              {/* Major rotation section */}
                              <div style={{ 
                                width: `${(majorWeeks / totalWeeks) * 100}%`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                {majorResidents.length > 0 ? (
                                  majorResidents.map((osr, idx) => {
                                    const rotation = osr.majorRotation || (osr.rotations && osr.rotations.major) || '';
                                    if (!rotation) return null;
                                    const rotationColor = rotationColors[rotation] || '#6B7280';
                                    return (
                                      <div 
                                        key={idx}
                                        className="custom-rotation-link-btn d-flex flex-row align-items-center justify-content-center gap-2"
                                        style={{
                                          backgroundColor: rotationColor,
                                          color: 'white',
                                          textAlign: 'center',
                                          fontWeight: '600',
                                          fontSize: '0.75rem',
                                          borderRadius: '6px',
                                          padding: '6px 4px',
                                          minHeight: '40px',
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexDirection: 'column',
                                          gap: '2px'
                                        }}
                                      >
                                        <span>{getRotationLabel(rotation)}</span>
                                        <div className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                          {osr.residentName}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div style={{ minHeight: '40px', width: '100%' }}></div>
                                )}
                              </div>
                              
                              {/* Minor rotation section */}
                              <div style={{ 
                                width: `${(minorWeeks / totalWeeks) * 100}%`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                {minorResidents.length > 0 ? (
                                  minorResidents.map((osr, idx) => {
                                    const rotation = osr.minorRotation || (osr.rotations && osr.rotations.minor) || '';
                                    if (!rotation) return null;
                                    const rotationColor = rotationColors[rotation] || '#6B7280';
                                    return (
                                      <div 
                                        key={idx}
                                        className="custom-rotation-link-btn d-flex flex-row align-items-center justify-content-center gap-2"
                                        style={{
                                          backgroundColor: rotationColor,
                                          color: 'white',
                                          textAlign: 'center',
                                          fontWeight: '600',
                                          fontSize: '0.75rem',
                                          borderRadius: '6px',
                                          padding: '6px 4px',
                                          minHeight: '40px',
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexDirection: 'column',
                                          gap: '2px'
                                        }}
                                      >
                                        <span>{getRotationLabel(rotation)}</span>
                                        <div className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                          {osr.residentName}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div style={{ minHeight: '40px', width: '100%' }}></div>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })()}
              </tbody>
            </Table>
          </Col>
          <Col md={isBlockViewExpanded ? 1 : 2} sm={12} className="ps-md-0">
            <div 
              className="border-start no-border-start" 
              style={{ padding: '16px', backgroundColor: 'var(--bg-light)', height: '100%' }}
              onDragOver={isRotationEditMode ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              } : undefined}
              onDrop={isRotationEditMode ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedRotation && draggedRotation.sourceType === 'block') {
                  const newRotationSchedules = JSON.parse(JSON.stringify(getCurrentRotationSchedules()));
                  
                  let pgyLevel = null;
                  Object.entries(newRotationSchedules).forEach(([pgy, scheduleData]) => {
                    if (scheduleData.schedule && scheduleData.schedule[draggedRotation.residentId]) {
                      pgyLevel = pgy;
                    }
                  });
                  
                  if (pgyLevel) {
                    const scheduleData = newRotationSchedules[pgyLevel];
                    const blockPatternValue = scheduleData.blockPattern || blockPattern || '4-1';
                    const [majorWeeks, minorWeeks] = blockPatternValue.split('-').map(Number);
                    const blockDurationValue = scheduleData.blockDuration || blockDuration || 5;
                    const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
                    
                    let sourceBlockWeeks = [];
                    for (let i = 0; i < weeks.length; i += blockDurationValue) {
                      const blockWeeks = weeks.slice(i, i + blockDurationValue);
                      if (blockWeeks.some(w => w.start === draggedRotation.blockStart)) {
                        sourceBlockWeeks = blockWeeks;
                        break;
                      }
                    }
                    
                    if (sourceBlockWeeks.length > 0) {
                      const sourceMajorWeeks = sourceBlockWeeks.slice(0, majorWeeks);
                      const sourceMinorWeeks = sourceBlockWeeks.slice(majorWeeks);
                      const sourceWeeks = draggedRotation.columnType === 'major' ? sourceMajorWeeks : sourceMinorWeeks;
                      
                      const weeksWithRotation = sourceWeeks.filter(week => 
                        scheduleData.schedule[draggedRotation.residentId].rotations[week.start] === draggedRotation.sourceRotation
                      );
                      
                      if (draggedRotation.columnType === 'major' && majorWeeks === 4 && weeksWithRotation.length === 2) {
                        const otherWeeks = sourceMajorWeeks.filter(w => !weeksWithRotation.includes(w));
                        const otherRotation = otherWeeks.length > 0 ? scheduleData.schedule[draggedRotation.residentId].rotations[otherWeeks[0].start] : null;
                        
                        weeksWithRotation.forEach(week => {
                          scheduleData.schedule[draggedRotation.residentId].rotations[week.start] = null;
                        });
                        
                        if (otherRotation && otherWeeks.length === 2) {
                          sourceMajorWeeks.forEach(week => {
                            scheduleData.schedule[draggedRotation.residentId].rotations[week.start] = otherRotation;
                          });
                        }
                      } else {
                        weeksWithRotation.forEach(week => {
                          scheduleData.schedule[draggedRotation.residentId].rotations[week.start] = null;
                        });
                      }
                      
                      // Note: This drop handler would need to update state via a callback
                      // For now, it's handled by the parent component's drag handlers
                    }
                  }
                }
              } : undefined}
            >
              <h6 className="mb-3" style={{ fontSize: '0.9rem', fontWeight: '600' }}>Rotation Types:</h6>
              <div className="d-flex flex-row flex-wrap flex-md-column flex-md-nowrap gap-2">
                {Array.from(allRotations).map(rotation => {
                  const config = getCurrentRotationSchedules()[pgyLevel]?.blockRotations?.[rotation];
                  const columnType = config?.column || 'major';
                  const totalWeeks = config?.totalWeeks || 0;
                  
                  return (
                    <div 
                      key={rotation} 
                      className="d-flex align-items-center gap-2"
                      draggable={isRotationEditMode}
                      onDragStart={isRotationEditMode ? (e) => handleRotationTypeDragStart(e, rotation, columnType) : undefined}
                      onSelectStart={(e) => {
                        if (isRotationEditMode) {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        cursor: isRotationEditMode ? 'grab' : 'default',
                        padding: '4px',
                        borderRadius: isRotationEditMode ? '4px' : '0',
                        backgroundColor: isRotationEditMode ? 'var(--gray-200)' : 'transparent',
                        userSelect: isRotationEditMode ? 'none' : 'auto',
                        WebkitUserSelect: isRotationEditMode ? 'none' : 'auto',
                      }}
                    >
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: rotationColors[rotation] || '#6B7280',
                          borderRadius: '3px',
                          flexShrink: 0
                        }}
                      />
                      <div className="d-flex flex-column" style={{ lineHeight: '1.2' }}>
                        <span className="small" style={{ fontSize: '0.8rem' }}>{abbreviateRotation(rotation)}</span>
                        {totalWeeks > 0 && (
                          <span className={`text-muted ${isRotationEditMode ? 'd-none' : ''}`} style={{ fontSize: '0.7rem' }}>{totalWeeks} week{totalWeeks !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {isRotationEditMode && (
                <Alert variant="warning" className="mt-3">
                    <small>Drag and drop a rotation to a block cell to add or split a block. Click on the rotation label to change the rotation type.</small>
                </Alert>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default RotationScheduleTab;

